import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import { useToast } from '@/contexts/ToastContext';
import { useWarehouseStore } from '@/stores/warehouseStore';
import { exportToExcel } from '@/utils/exportUtils';
import {
  scrapApi, num, SCRAP_SOURCE_LABEL, SCRAP_REASON_LABEL,
  type ScrapEntry, type ScrapSummary, type ScrapStatus, type ScrapSource,
} from '@/api/scrap.api';
import { StatusBadge, SourceTag, inr, qtyText, dateText } from './components/scrapUi';
import NewScrapModal from './components/NewScrapModal';
import ReviewScrapModal from './components/ReviewScrapModal';
import DisposeScrapModal from './components/DisposeScrapModal';
import ScrapDetailDrawer from './components/ScrapDetailDrawer';

// Scrap management: every lot of scrap (production rejections, incoming rejections, process waste,
// write-offs), its approval into the scrap yard and its disposal.

type Period = 'THIS_MONTH' | 'LAST_MONTH' | 'THIS_YEAR' | 'ALL' | 'CUSTOM';

const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

function periodRange(p: Period, from: string, to: string): { from?: string; to?: string } {
  const now = new Date();
  if (p === 'THIS_MONTH') return { from: iso(new Date(now.getFullYear(), now.getMonth(), 1)), to: iso(now) };
  if (p === 'LAST_MONTH') return { from: iso(new Date(now.getFullYear(), now.getMonth() - 1, 1)), to: iso(new Date(now.getFullYear(), now.getMonth(), 0)) };
  if (p === 'THIS_YEAR') {
    // Indian fiscal year: April – March
    const start = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
    return { from: `${start}-04-01`, to: iso(now) };
  }
  if (p === 'CUSTOM') return { from: from || undefined, to: to || undefined };
  return {};
}

const STATUS_TABS: { value: ScrapStatus | 'ALL'; label: string }[] = [
  { value: 'ALL', label: 'All' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'APPROVED', label: 'In Scrap Yard' },
  { value: 'DISPOSED', label: 'Disposed' },
  { value: 'REJECTED', label: 'Rejected' },
];

export default function ScrapManagementPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const [params] = useSearchParams();
  const { selectedWarehouseId } = useWarehouseStore();
  const [entries, setEntries] = useState<ScrapEntry[]>([]);
  const [summary, setSummary] = useState<ScrapSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [canApprove, setCanApprove] = useState(false);

  // Links from the scrap yard / production orders open a status and a search (any date)
  const linkedStatus = params.get('status') as ScrapStatus | null;
  const [period, setPeriod] = useState<Period>(linkedStatus || params.get('search') ? 'ALL' : 'THIS_MONTH');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [status, setStatus] = useState<ScrapStatus | 'ALL'>(linkedStatus && STATUS_TABS.some((t) => t.value === linkedStatus) ? linkedStatus : 'ALL');
  const [source, setSource] = useState<ScrapSource | 'ALL'>('ALL');
  const [search, setSearch] = useState(params.get('search') || '');

  const [newOpen, setNewOpen] = useState(false);
  const [review, setReview] = useState<{ entry: ScrapEntry; mode: 'approve' | 'reject' } | null>(null);
  const [disposing, setDisposing] = useState<ScrapEntry | null>(null);
  const [viewId, setViewId] = useState<string | null>(null);
  const [drawerVersion, setDrawerVersion] = useState(0);

  const range = useMemo(() => periodRange(period, customFrom, customTo), [period, customFrom, customTo]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const filters = { ...range, source, warehouseId: selectedWarehouseId || undefined };
      const [list, sum] = await Promise.all([
        scrapApi.list({ ...filters, status }),
        scrapApi.summary(filters),
      ]);
      setEntries(list.data || []);
      setSummary(sum.data || null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not load scrap entries');
    } finally {
      setLoading(false);
    }
  }, [range, source, status, selectedWarehouseId, toast]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    scrapApi.myRights().then((r) => setCanApprove(!!r.data?.canApprove)).catch(() => setCanApprove(false));
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter((e) => [e.scrap_number, e.item_name, e.source_number, e.buyer_name, e.ncr_number]
      .some((v) => String(v || '').toLowerCase().includes(q)));
  }, [entries, search]);

  const afterChange = () => {
    setReview(null);
    setDisposing(null);
    setDrawerVersion((v) => v + 1);
    void load();
  };

  const exportRows = () => {
    const rows = filtered.map((e) => ({
        'Scrap No': e.scrap_number,
        Date: dateText(e.entry_date),
        Source: SCRAP_SOURCE_LABEL[e.source_type],
        Reference: e.source_number || '',
        Stage: e.stage_number != null ? `${e.stage_number} ${e.stage_name || ''}`.trim() : '',
        Item: e.item_name,
        Qty: num(e.qty),
        Unit: e.unit || '',
        Reason: SCRAP_REASON_LABEL[e.reason],
        'Book Value': num(e.book_value),
        'Scrap Value': num(e.scrap_value),
        Status: e.status,
        Disposal: e.disposal_method || '',
        Buyer: e.buyer_name || '',
        'Sale Amount': num(e.sale_amount),
        Loss: e.loss,
        Location: e.scrap_location,
    }));
    exportToExcel(rows, Object.keys(rows[0] || {}).map((k) => ({ header: k, key: k })), 'scrap-register', 'Scrap Register');
  };

  const cards = summary ? [
    { key: 'PENDING', label: 'Pending', value: summary.pending.count, sub: `${inr(summary.pending.bookValue)} material cost`, icon: 'ri-time-line', cls: 'bg-amber-50 text-amber-600' },
    { key: 'APPROVED', label: 'In Scrap Yard', value: summary.approved.count, sub: `${inr(summary.approved.scrapValue)} scrap value`, icon: 'ri-inbox-archive-line', cls: 'bg-indigo-50 text-[#4f46e5]' },
    { key: 'DISPOSED', label: 'Disposed', value: summary.disposed.count, sub: `${inr(summary.disposed.saleAmount)} sold`, icon: 'ri-checkbox-circle-line', cls: 'bg-green-50 text-green-600' },
    { key: 'LOSS', label: 'Scrap Loss (P&L)', value: inr(summary.loss), sub: 'Cost not recovered', icon: 'ri-arrow-down-line', cls: 'bg-red-50 text-red-600' },
    { key: 'RECOVERY', label: 'Recovered', value: inr(summary.recovery), sub: 'From scrap sales (ex GST)', icon: 'ri-hand-coin-line', cls: 'bg-emerald-50 text-emerald-600' },
  ] : [];

  return (
    <AppLayout>
      <div className="p-6 space-y-5 w-full">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-xl font-bold text-[#1e293b]">Scrap Management</h1>
            <p className="text-sm text-[#64748b] mt-0.5">Rejected, wasted and written-off material — from the scrap yard to sale or destruction</p>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => navigate('/inventory/scrap-yard')}
              className="h-9 px-3 rounded-lg border border-[#e2e8f0] bg-white text-sm font-medium text-[#475569] hover:bg-[#f8fafc] flex items-center gap-1.5 cursor-pointer">
              <i className="ri-inbox-archive-line" /> Scrap Yard
            </button>
            <button type="button" onClick={() => navigate('/reports/scrap')}
              className="h-9 px-3 rounded-lg border border-[#e2e8f0] bg-white text-sm font-medium text-[#475569] hover:bg-[#f8fafc] flex items-center gap-1.5 cursor-pointer">
              <i className="ri-bar-chart-box-line" /> Reports
            </button>
            <button type="button" onClick={exportRows} disabled={!filtered.length}
              className="h-9 px-3 rounded-lg border border-[#e2e8f0] bg-white text-sm font-medium text-[#475569] hover:bg-[#f8fafc] disabled:opacity-50 flex items-center gap-1.5 cursor-pointer">
              <i className="ri-file-excel-2-line" /> Export
            </button>
            <button type="button" onClick={() => setNewOpen(true)}
              className="h-9 px-4 rounded-lg bg-[#4f46e5] text-white text-sm font-semibold hover:bg-indigo-700 flex items-center gap-1.5 cursor-pointer">
              <i className="ri-add-line" /> New Scrap Entry
            </button>
          </div>
        </div>

        {!canApprove && (
          <div className="flex items-start gap-2 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-blue-700">
            <i className="ri-information-line mt-px" />
            You can raise scrap entries. A production manager or admin with the "Approve / Dispose Scrap" permission values, approves and disposes of them.
          </div>
        )}

        {/* Summary */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {cards.map((c) => (
            <button key={c.key} type="button"
              onClick={() => (['PENDING', 'APPROVED', 'DISPOSED'].includes(c.key) ? setStatus(c.key as ScrapStatus) : undefined)}
              className={`text-left bg-white border rounded-xl p-4 transition-colors ${status === c.key ? 'border-[#4f46e5]' : 'border-[#e2e8f0]'} ${['PENDING', 'APPROVED', 'DISPOSED'].includes(c.key) ? 'cursor-pointer hover:border-[#4f46e5]/50' : 'cursor-default'}`}>
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">{c.label}</p>
                <span className={`w-7 h-7 rounded-lg flex items-center justify-center ${c.cls}`}><i className={c.icon} /></span>
              </div>
              <p className="text-2xl font-bold text-[#1e293b] mt-1">{c.value}</p>
              <p className="text-[11px] text-[#94a3b8]">{c.sub}</p>
            </button>
          ))}
        </div>

        {/* Filters */}
        <div className="bg-white border border-[#e2e8f0] rounded-xl p-3 flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-lg border border-[#e2e8f0] p-0.5 bg-[#f8fafc]">
            {STATUS_TABS.map((t) => (
              <button key={t.value} type="button" onClick={() => setStatus(t.value)}
                className={`h-8 px-3 rounded-md text-xs font-semibold cursor-pointer ${status === t.value ? 'bg-white text-[#4f46e5] shadow-sm' : 'text-[#64748b] hover:text-[#1e293b]'}`}>
                {t.label}
              </button>
            ))}
          </div>
          <select value={source} onChange={(e) => setSource(e.target.value as ScrapSource | 'ALL')}
            className="h-9 px-2.5 rounded-lg border border-[#e2e8f0] text-sm bg-white focus:outline-none focus:border-[#4f46e5]">
            <option value="ALL">All sources</option>
            {(Object.keys(SCRAP_SOURCE_LABEL) as ScrapSource[]).map((s) => <option key={s} value={s}>{SCRAP_SOURCE_LABEL[s]}</option>)}
          </select>
          <select value={period} onChange={(e) => setPeriod(e.target.value as Period)}
            className="h-9 px-2.5 rounded-lg border border-[#e2e8f0] text-sm bg-white focus:outline-none focus:border-[#4f46e5]">
            <option value="THIS_MONTH">This month</option>
            <option value="LAST_MONTH">Last month</option>
            <option value="THIS_YEAR">This financial year</option>
            <option value="ALL">All time</option>
            <option value="CUSTOM">Custom…</option>
          </select>
          {period === 'CUSTOM' && (
            <>
              <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="h-9 px-2 rounded-lg border border-[#e2e8f0] text-sm" />
              <span className="text-xs text-[#94a3b8]">to</span>
              <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="h-9 px-2 rounded-lg border border-[#e2e8f0] text-sm" />
            </>
          )}
          <div className="relative flex-1 min-w-[200px]">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-[#94a3b8]" />
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search scrap no, item, order, GRN, buyer…"
              className="w-full h-9 pl-9 pr-3 rounded-lg border border-[#e2e8f0] text-sm focus:outline-none focus:border-[#4f46e5]" />
          </div>
        </div>

        {/* Register */}
        <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-max text-sm">
              <thead>
                <tr className="bg-[#f8fafc] border-b border-[#e2e8f0]">
                  {['Scrap No', 'Item', 'Qty', 'Source', 'Reason', 'Material Cost', 'Scrap Value', 'Status', ''].map((h) => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-[#64748b] uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={9} className="px-4 py-12 text-center text-[#94a3b8]"><i className="ri-loader-4-line animate-spin text-3xl text-[#4f46e5] block mb-2" />Loading scrap…</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={9} className="px-4 py-12 text-center">
                    <i className="ri-delete-bin-6-line text-4xl text-[#e2e8f0] block mb-2" />
                    <p className="text-sm text-[#94a3b8]">No scrap in this period</p>
                  </td></tr>
                ) : filtered.map((e) => (
                  <tr key={e.id} className="border-b border-[#f1f5f9] hover:bg-[#f8fafc] transition-colors">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <button type="button" onClick={() => setViewId(e.id)} className="font-mono text-xs font-semibold text-[#4f46e5] hover:underline cursor-pointer">{e.scrap_number}</button>
                      <p className="text-[11px] text-[#94a3b8]">{dateText(e.entry_date)}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-[#1e293b] max-w-[220px] truncate" title={e.item_name}>{e.item_name}</p>
                      <p className="text-[11px] text-[#94a3b8]">{e.scrap_location}</p>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-[#1e293b]">{qtyText(e.qty)} <span className="text-xs text-[#94a3b8]">{e.unit}</span></td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <SourceTag source={e.source_type} />
                      <p className="text-[11px] text-[#64748b] mt-0.5">
                        {e.source_number || '—'}{e.stage_number != null ? ` · Stage ${e.stage_number}` : ''}{e.ncr_number ? ` · ${e.ncr_number}` : ''}
                      </p>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-xs text-[#475569]">{SCRAP_REASON_LABEL[e.reason]}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-[#475569]">{inr(e.book_value)}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <p className="font-semibold text-[#1e293b]">{inr(e.scrap_value)}</p>
                      {e.status === 'DISPOSED' && e.disposal_method === 'SOLD' && (
                        <p className="text-[11px] text-green-700">sold {inr(e.sale_amount)}</p>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap"><StatusBadge status={e.status} method={e.disposal_method} /></td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        {canApprove && e.status === 'PENDING' && (
                          <button type="button" onClick={() => setReview({ entry: e, mode: 'approve' })}
                            className="h-7 px-2.5 rounded-md bg-[#4f46e5] text-white text-xs font-semibold hover:bg-indigo-700 cursor-pointer">Review</button>
                        )}
                        {canApprove && e.status === 'APPROVED' && (
                          <button type="button" onClick={() => setDisposing(e)}
                            className="h-7 px-2.5 rounded-md border border-[#4f46e5] text-[#4f46e5] text-xs font-semibold hover:bg-indigo-50 cursor-pointer">Dispose</button>
                        )}
                        <button type="button" onClick={() => setViewId(e.id)} title="View"
                          className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-indigo-50 text-[#64748b] hover:text-[#4f46e5] cursor-pointer">
                          <i className="ri-eye-line" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <NewScrapModal open={newOpen} onClose={() => setNewOpen(false)} onCreated={() => { setNewOpen(false); void load(); }} />
      <ReviewScrapModal entry={review?.entry ?? null} mode={review?.mode ?? 'approve'} onClose={() => setReview(null)} onDone={afterChange} />
      <DisposeScrapModal entry={disposing} onClose={() => setDisposing(null)} onDone={afterChange} />
      <ScrapDetailDrawer
        entryId={viewId}
        version={drawerVersion}
        canApprove={canApprove}
        onClose={() => setViewId(null)}
        onApprove={(e) => setReview({ entry: e, mode: 'approve' })}
        onReject={(e) => setReview({ entry: e, mode: 'reject' })}
        onDispose={(e) => setDisposing(e)}
      />
    </AppLayout>
  );
}
